import { useState, useEffect } from "react";
import { supabase } from '../supabase';
import { logActivity } from './ActivityLog';
import { CustomerSelect } from './Customers';
import { composeOfferHtml, quoteTotal, extractArtists } from './offer/composeOffer';
import { loadSections, toSectionMap } from './portal/sectionStore';
import { compressImage, EXT_BY_TYPE, makeAdoptUrl } from './imageTools';

const MODULE_TYPES = [
  { type:"text", label:"Tekst" }, { type:"image", label:"Bilde" },
  { type:"gallery", label:"Galleri" }, { type:"split", label:"Split" }, { type:"audio", label:"Lyd" },
];
const TYPE_LABEL = { text:"Tekst", image:"Bilde", gallery:"Galleri", split:"Split", audio:"Lyd", section:"Standardseksjon" };
// Innholds-seksjoner fra portalen som kan settes inn i et tilbud
const OFFER_SECTION_KEYS = ["om","velkomst","artister","produksjoner","pakker","materiell"];

const C = {
  bg:"#0e0e0e", sidebar:"#0a0a0a", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};
const card = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp  = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn  = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl  = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const secTitle = t => <div style={{fontSize:11,color:C.muted,marginBottom:12,letterSpacing:0.5,textTransform:"uppercase"}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;
const OFFER_BASE = "https://app.royal.show/t/";
const BUCKET = "portals";
const fmtNok = n => (Number(n)||0).toLocaleString("nb-NO").replace(/ /g," ");

function slugify(str){ return (str||"").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"").replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,""); }
function token(n=4){ const c="abcdefghijkmnpqrstuvwxyz23456789"; let o=""; for(let i=0;i<n;i++)o+=c[Math.floor(Math.random()*c.length)]; return o; }
const newId = () => "k"+token(5);

const STATUS = { draft:{label:"Utkast",color:C.muted}, sent:{label:"Sendt",color:C.blue}, accepted:{label:"Akseptert",color:C.green}, declined:{label:"Avslått",color:C.red} };

const DEFAULT_CONTRACT = `Dette tilbudet gjelder levering av underholdning fra Royal til avtalt dato og sted.

• Booking er bindende ved aksept av dette tilbudet.
• Endelig program, antall artister og format avtales i god tid før arrangementet.
• Reise, opphold og eventuelle tilleggstjenester faktureres etter avtale.
• Avbestilling senere enn 30 dager før arrangementet faktureres 50 %; senere enn 14 dager 100 %.
• Betaling: 14 dager netto etter arrangementet, om ikke annet er avtalt. Alle priser er eks. mva.`;

const DEFAULT_RIDER = `Teknisk:
• Scene/gulv egnet for dans (flatt, rent, tørt).
• Lyd: PA tilpasset lokalet + mulighet for å spille av lyd fra vår enhet.
• Strøm i nærheten av sceneområdet.

Hospitality:
• Avlukket garderobe med speil og mulighet for omkledning.
• Vann og lett servering til artistene.`;

const DEFAULT_CONTRACT_EN = `This offer covers the delivery of entertainment from Royal at the agreed date and venue.

• The booking becomes binding upon acceptance of this offer.
• The final programme, number of artists and format are agreed in good time before the event.
• Travel, accommodation and any additional services are invoiced as agreed.
• Cancellation later than 30 days before the event is charged at 50%; later than 14 days, 100%.
• Payment: net 14 days after the event, unless otherwise agreed. All prices are excl. VAT.`;

const DEFAULT_RIDER_EN = `Technical:
• Stage/floor suitable for dance (flat, clean, dry).
• Sound: a PA suited to the venue + the ability to play audio from our device.
• Power near the stage area.

Hospitality:
• A private dressing room with a mirror and space to change.
• Water and light refreshments for the artists.`;

const LANGS = [{ code:"no", label:"Norsk" }, { code:"en", label:"English" }];
const defaultsFor = (lang) => lang === "en"
  ? { contract: DEFAULT_CONTRACT_EN, rider: DEFAULT_RIDER_EN }
  : { contract: DEFAULT_CONTRACT, rider: DEFAULT_RIDER };
const isDefaultText = (s) => [DEFAULT_CONTRACT, DEFAULT_RIDER, DEFAULT_CONTRACT_EN, DEFAULT_RIDER_EN].includes((s || "").trim()) || !(s || "").trim();

export function OffersPage({ user, customers=[], projects=[], onCreateCustomer, onOpenProject, isMobile }) {
  const [view, setView] = useState("list");
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ msg:"", ok:true });
  const showToast = (m,ok=true)=>{ setToast({msg:m,ok}); setTimeout(()=>setToast({msg:"",ok:true}),2800); };

  // Builder-state
  const [editing, setEditing] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [concept, setConcept] = useState([]);   // typed modules
  const [sectionsLib, setSectionsLib] = useState([]);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [contractHtml, setContractHtml] = useState(DEFAULT_CONTRACT);
  const [riderHtml, setRiderHtml] = useState(DEFAULT_RIDER);
  const [language, setLanguage] = useState("no");
  const [validUntil, setValidUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);   // HTML-streng for forhåndsvisning
  const [acceptance, setAcceptance] = useState(null);   // {offer, rec, loading} for aksept-modal
  const [present, setPresent] = useState(null);   // {offer, mods[], pris, kontrakt, rider, exists, link, busy, checking} for presentasjonslenke-modal
  const [views, setViews] = useState({});         // slug -> visninger
  const [viewsFor, setViewsFor] = useState(null); // tilbudet vist i visnings-modalen
  const [trash, setTrash] = useState([]);         // myk-slettede tilbud
  const [showTrash, setShowTrash] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const fmtDate = d => d ? new Date(d+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"long",year:"numeric"}) : "";
  const custName = id => customers.find(c=>c.id===id)?.name || "";
  const custProjects = id => projects.filter(p => p.customer_id===id && !p.archived);
  const projById = id => projects.find(p=>p.id===id);

  // kjøres kun ved oppstart
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ loadOffers(); loadSections().then(setSectionsLib); loadTemplates(); }, []);
  const loadOffers = async () => {
    setLoading(true);
    const { data } = await supabase.from('offers').select('*').order('created_at',{ascending:false});
    const all = data||[];
    setOffers(all.filter(o=>!o.deleted_at));
    setTrash(all.filter(o=>o.deleted_at));
    setLoading(false);
    loadViews(all.map(o=>o.slug));
  };

  // Visninger per tilbud. Presentasjonslenken (<slug>-vis) telles med på samme tilbud.
  const loadViews = async (slugs) => {
    if(!slugs.length) return;
    const wanted = [...slugs, ...slugs.map(s=>`${s}-vis`)];
    const { data } = await supabase.from('offer_views').select('*')
      .in('slug', wanted).order('viewed_at',{ascending:false}).limit(2000);
    const map = {};
    (data||[]).forEach(v=>{
      const base = v.slug.endsWith("-vis") ? v.slug.slice(0,-4) : v.slug;
      (map[base] = map[base] || []).push(v);
    });
    setViews(map);
  };

  // Gjentatte visninger fra samme sesjon innen 30 min telles som én åpning.
  const viewStats = (slug) => {
    const rows = (views[slug]||[]).filter(v=>v.js_confirmed);
    if(!rows.length) return { opens:0, last:null };
    const asc = [...rows].sort((a,b)=>new Date(a.viewed_at)-new Date(b.viewed_at));
    let opens = 1;
    for(let i=1;i<asc.length;i++){
      const gap = new Date(asc[i].viewed_at) - new Date(asc[i-1].viewed_at);
      if(gap > 30*60*1000 || asc[i].ip !== asc[i-1].ip) opens++;
    }
    return { opens, last: asc[asc.length-1].viewed_at };
  };
  const loadTemplates = async () => {
    const { data } = await supabase.from('offer_templates').select('*').order('created_at',{ascending:false});
    setTemplates(data||[]);
  };

  const partnerPrice = (cust) => {
    if(!cust) return 17500;
    return Math.round((Number(cust.guide_price)||0) * (1 - (Number(cust.partner_discount_pct)||0)/100));
  };

  // src = innholdskilde (tilbud/mal/null), editingRow = raden som redigeres (null = nytt tilbud)
  const openBuilder = (src=null, editingRow=null) => {
    const lang = src?.language || customers.find(c=>c.id===src?.customer_id)?.language || "no";
    const def = defaultsFor(lang);
    setEditing(editingRow);
    setCustomerId(src?.customer_id || "");
    setProjectId(src?.project_id || "");
    setTitle(src?.title || "");
    setConcept((src?.concept||[]).map(b=>({
      id:b.id||newId(), type:b.type||(b.key?"section":"image"), key:b.key, label:b.label, artists:Array.isArray(b.artists)?b.artists:null,
      eyebrow:b.eyebrow||"", title:b.title||"", text:b.text||"", image_url:b.image_url||"", video_url:b.video_url||"", audio_url:b.audio_url||"",
      images:(b.images||[]).map(u=>({ image_url:typeof u==="string"?u:(u.image_url||""), _file:null })), _file:null,
    })));
    setLines((src?.quote?.lines)||[]);
    setNotes(src?.quote?.notes || "");
    setContractHtml(src?.contract_html ?? def.contract);
    setRiderHtml(src?.rider_html ?? def.rider);
    setLanguage(lang);
    setValidUntil(src?.valid_until || "");
    setView("builder");
  };

  // Bytt språk: bytt standardmaler hvis admin ikke har redigert dem
  const switchLanguage = (lang) => {
    const def = defaultsFor(lang);
    if(isDefaultText(contractHtml)) setContractHtml(def.contract);
    if(isDefaultText(riderHtml)) setRiderHtml(def.rider);
    setLanguage(lang);
  };

  // Moduler (typed)
  const addModule = (type) => setConcept(a=>[...a,{ id:newId(), type, eyebrow:"", title:"", text:"", image_url:"", video_url:"", audio_url:"", images:[], _file:null }]);
  const addSection = (key) => { const sec = sectionsLib.find(s=>s.key===key); if(!sec) return; setConcept(a=>[...a,{ id:newId(), type:"section", key, label:sec.label }]); };
  const updModule = (i,p) => setConcept(a=>a.map((b,j)=>j===i?{...b,...p}:b));
  const rmModule = i => setConcept(a=>a.filter((_,j)=>j!==i));
  const moveModule = (i,d) => setConcept(a=>{ const j=i+d; if(j<0||j>=a.length)return a; const n=[...a]; [n[i],n[j]]=[n[j],n[i]]; return n; });
  // Galleri-bilder i en modul
  const addGalleryImg = (i,file) => setConcept(a=>a.map((b,j)=> j===i ? {...b, images:[...(b.images||[]), { image_url:"", _file:file }]} : b));
  const rmGalleryImg = (i,k) => setConcept(a=>a.map((b,j)=> j===i ? {...b, images:(b.images||[]).filter((_,x)=>x!==k)} : b));
  // Prislinjer
  const addLine = () => setLines(a=>[...a,{ desc:"", qty:1, unit_price: partnerPrice(customers.find(c=>c.id===customerId)) }]);
  const updLine = (i,k,v) => setLines(a=>a.map((l,j)=>j===i?{...l,[k]:v}:l));
  const rmLine = i => setLines(a=>a.filter((_,j)=>j!==i));
  const total = lines.reduce((s,l)=>s+(Number(l.qty)||0)*(Number(l.unit_price)||0),0);

  const onCustomer = (id) => {
    setCustomerId(id||"");
    setProjectId("");  // nullstill prosjekt (filtreres på kunde)
    const cust = customers.find(c=>c.id===id);
    if(cust && lines.length) setLines(a=>a.map(l=>({ ...l, unit_price: l.unit_price || partnerPrice(cust) })));
    // arv kundens språk (kun for nye tilbud, og bytt maler hvis urørt)
    if(cust && !editing && cust.language && cust.language!==language) switchLanguage(cust.language);
  };

  const saveBuilder = async () => {
    if(!customerId){ showToast("Velg en kunde.", false); return; }
    if(!projectId){ showToast("Velg et prosjekt.", false); return; }
    setSaving(true);
    const name = custName(customerId);
    const slug = editing?.slug || `${slugify(name)}-tilbud-${token()}`;
    const storage_path = `offers/${slug}/index.html`;
    try {
      // last opp ventende bilder (enkelt-bilde + galleri)
      const uploadFile = async (file, fallbackExt='jpg', errLabel='Bilde') => {
        const src = errLabel==='Lyd' ? file : await compressImage(file);
        const ext = EXT_BY_TYPE[src.type] || (file.name.split('.').pop()||fallbackExt).toLowerCase().replace(/[^a-z0-9]/g,'');
        const apath=`offers/${slug}/assets/${token(6)}.${ext}`;
        const { error } = await supabase.storage.from(BUCKET).upload(apath, src, { contentType:src.type||undefined });
        if(error) throw new Error(errLabel+"-opplasting feilet: "+error.message);
        return `/t/${slug}/assets/${apath.split('/').pop()}`;
      };
      // Filer arvet fra et annet tilbud (typisk via «Dupliser») kopieres inn i
      // dette tilbudets egen mappe, så tilbudet ikke er avhengig av et annet.
      const adoptUrl = makeAdoptUrl({ storage:supabase.storage, bucket:BUCKET, slug,
        urlPrefix:"t", dir:s=>`offers/${s}`, token });
      const work = [...concept];
      for(let i=0;i<work.length;i++){
        const b={...work[i]};
        if(b._file && b.type==="audio"){ b.audio_url = await uploadFile(b._file,'mp3','Lyd'); b._file=null; }
        else if(b._file){ b.image_url = await uploadFile(b._file); b._file=null; }
        b.image_url = await adoptUrl(b.image_url);
        b.audio_url = await adoptUrl(b.audio_url);
        if(b.images && b.images.length){
          const imgs=[];
          for(const im of b.images){ if(im._file) imgs.push(await uploadFile(im._file)); else if(im.image_url) imgs.push(await adoptUrl(im.image_url)); }
          b.images = imgs;
        }
        work[i]=b;
      }
      const conceptClean = work.map(b=> b.type==="section"
        ? { id:b.id, type:"section", key:b.key, label:b.label, ...(b.key==="artister"&&Array.isArray(b.artists)&&b.artists.length?{artists:b.artists}:{}) }
        : { id:b.id, type:b.type||"image", eyebrow:b.eyebrow||"", title:b.title||"", text:b.text||"", image_url:b.image_url||"", video_url:b.video_url||"", audio_url:b.audio_url||"", images:b.images||[] });
      const cleanLines = lines.filter(l=>(l.desc||"").trim()).map(l=>({ desc:l.desc, qty:Number(l.qty)||0, unit_price:Number(l.unit_price)||0 }));
      const quote = { lines:cleanLines, notes };
      const proj = projById(projectId);
      const eventLabel = proj ? `${proj.name}${proj.date?` · ${fmtDate(proj.date)}`:""}${proj.location?` · ${proj.location}`:""}` : "";

      // ferske seksjoner ved publisering (så endringer i standardseksjoner slår inn)
      const freshLib = await loadSections(); setSectionsLib(freshLib);
      const html = composeOfferHtml({ slug, customerName:name, title, eventLabel, modules:conceptClean, sectionMap:toSectionMap(freshLib), quote, contractHtml, riderHtml, language, validUntil:validUntil||null });
      const blob = new Blob([html], { type:"text/html" });
      const store = supabase.storage.from(BUCKET);
      const up = editing ? await store.update(storage_path, blob, { contentType:"text/html", cacheControl:"30" })
                         : await store.upload(storage_path, blob, { contentType:"text/html", cacheControl:"30" });
      if(up.error) throw new Error("Publisering feilet: "+up.error.message);

      const row = {
        slug, customer_id:customerId, project_id:projectId, title:title||null,
        status: (editing?.status==="accepted"||editing?.status==="declined") ? editing.status : "sent", language,
        valid_until: validUntil||null,
        concept:conceptClean, quote, contract_html:contractHtml, rider_html:riderHtml, storage_path,
      };
      if(editing){ const { error } = await supabase.from('offers').update(row).eq('id', editing.id); if(error) throw new Error("Kunne ikke lagre: "+error.message); }
      else { const { error } = await supabase.from('offers').insert({ ...row, created_by:user?.id||null }); if(error){ await store.remove([storage_path]); throw new Error("Kunne ikke lagre: "+error.message); } }

      logActivity(user, editing?'offer_updated':'offer_created', { customer:name, slug }, 'offers');
      showToast(editing ? "Tilbud oppdatert! 🎉" : "Tilbud opprettet! 🎉");
      setSaving(false); setView("list"); loadOffers();
    } catch(e){ showToast(e.message||"Noe gikk galt.", false); setSaving(false); }
  };

  const offerPage = o => o.storage_path || `offers/${o.slug}/index.html`;

  // Myk sletting: lenken dør, men raden og bildene blir liggende slik at
  // tilbudet kan hentes tilbake – og slik at duplikater ikke mister bildene.
  const deleteOffer = async (o) => {
    if(!window.confirm(`Legge tilbudet for «${custName(o.customer_id)}» i papirkurven?\n\nLenken slutter å virke umiddelbart, men tilbudet kan gjenopprettes.`)) return;
    await supabase.storage.from(BUCKET).remove([offerPage(o), `offers/${o.slug}-vis/index.html`]);
    const deleted_at = new Date().toISOString();
    const { error } = await supabase.from('offers').update({ deleted_at }).eq('id', o.id);
    if(error){ showToast("Kunne ikke slette.", false); return; }
    logActivity(user, 'offer_deleted', { customer:custName(o.customer_id), slug:o.slug }, 'offers');
    showToast("Lagt i papirkurven.");
    setOffers(p=>p.filter(x=>x.id!==o.id));
    setTrash(p=>[{...o, deleted_at}, ...p]);
  };

  // Gjenoppretting bygger siden på nytt fra tilbudets lagrede data.
  const restoreOffer = async (o) => {
    setRestoring(o.id);
    try {
      const fresh = await loadSections();
      const proj = projById(o.project_id);
      const eventLabel = proj ? `${proj.name}${proj.date?` · ${fmtDate(proj.date)}`:""}${proj.location?` · ${proj.location}`:""}` : "";
      const html = composeOfferHtml({
        slug:o.slug, customerName:custName(o.customer_id) || (o.language==="en"?"Client":"Kunde"),
        title:o.title, eventLabel, modules:o.concept||[], sectionMap:toSectionMap(fresh),
        quote:o.quote||{lines:[],notes:""}, contractHtml:o.contract_html||"", riderHtml:o.rider_html||"",
        language:o.language||"no", validUntil:o.valid_until||null,
      });
      const blob = new Blob([html], { type:"text/html" });
      const { error:upErr } = await supabase.storage.from(BUCKET)
        .upload(offerPage(o), blob, { contentType:"text/html", cacheControl:"30", upsert:true });
      if(upErr) throw new Error(upErr.message);
      const { error } = await supabase.from('offers').update({ deleted_at:null }).eq('id', o.id);
      if(error) throw new Error(error.message);
      logActivity(user, 'offer_restored', { customer:custName(o.customer_id), slug:o.slug }, 'offers');
      showToast("Tilbud gjenopprettet! 🎉");
      setTrash(p=>p.filter(x=>x.id!==o.id));
      setOffers(p=>[{...o, deleted_at:null}, ...p]);
    } catch(e){ showToast("Kunne ikke gjenopprette: "+(e.message||e), false); }
    setRestoring(null);
  };

  // Permanent sletting beholder bilder som andre tilbud fortsatt peker på.
  const purgeOffer = async (o) => {
    if(!window.confirm(`Slette tilbudet for «${custName(o.customer_id)}» permanent?\n\nDette kan ikke angres.`)) return;
    const { data: others } = await supabase.from('offers').select('concept').neq('id', o.id);
    const used = new Set();
    JSON.stringify(others||[]).replace(new RegExp(`/t/${o.slug}/assets/([a-z0-9.]+)`,'g'), (all,f)=>{ used.add(f); return all; });
    const { data: assets } = await supabase.storage.from(BUCKET).list(`offers/${o.slug}/assets`, { limit:1000 });
    const keep = (assets||[]).filter(f=>used.has(f.name)).length;
    const paths = [offerPage(o), `offers/${o.slug}-vis/index.html`,
      ...(assets||[]).filter(f=>!used.has(f.name)).map(f=>`offers/${o.slug}/assets/${f.name}`)];
    await supabase.storage.from(BUCKET).remove([...new Set(paths)]);
    const { error } = await supabase.from('offers').delete().eq('id', o.id);
    if(error){ showToast("Kunne ikke slette.", false); return; }
    logActivity(user, 'offer_purged', { customer:custName(o.customer_id), slug:o.slug }, 'offers');
    showToast(keep>0 ? `Slettet permanent. ${keep} bilde${keep>1?"r":""} beholdt – brukes av andre tilbud.` : "Slettet permanent.");
    setTrash(p=>p.filter(x=>x.id!==o.id));
  };
  const copyLink = slug => { navigator.clipboard.writeText(OFFER_BASE+slug); showToast("Lenke kopiert!"); };

  // ---- Presentasjonslenke: en pris-/signeringsfri variant på /t/<slug>-vis ----
  // Bygges fra tilbudets lagrede data; rører ALDRI det opprinnelige (ev. signerte) tilbudet.
  const moduleLabel = (m) => m.type==="section"
    ? (m.label||"Seksjon")
    : `${TYPE_LABEL[m.type||"image"]}${m.title?` · ${m.title}`:(m.eyebrow?` · ${m.eyebrow}`:"")}`;
  const openPresent = async (o) => {
    const mods = (o.concept||[]).map((m,i)=>({ key:i, label:moduleLabel(m), on:true }));
    setPresent({ offer:o, mods, pris:false, kontrakt:false, rider:true, exists:false, link:null, busy:false, checking:true });
    const { data } = await supabase.storage.from(BUCKET).list(`offers/${o.slug}-vis`, { limit:1 });
    const exists = (data||[]).some(f=>f.name==="index.html");
    setPresent(p=> p && p.offer.id===o.id ? { ...p, exists, link: exists?`${o.slug}-vis`:null, checking:false } : p);
  };
  const toggleMod = (key) => setPresent(p=> p ? { ...p, mods:p.mods.map(m=>m.key===key?{...m,on:!m.on}:m) } : p);
  const genPresent = async () => {
    const p = present; if(!p) return;
    setPresent({ ...p, busy:true });
    try {
      const o = p.offer;
      const name = custName(o.customer_id) || (o.language==="en"?"Client":"Kunde");
      const selKeys = new Set(p.mods.filter(m=>m.on).map(m=>m.key));
      const selModules = (o.concept||[]).filter((m,i)=>selKeys.has(i));
      const fresh = await loadSections();
      const proj = projById(o.project_id);
      const eventLabel = proj ? `${proj.name}${proj.date?` · ${fmtDate(proj.date)}`:""}${proj.location?` · ${proj.location}`:""}` : "";
      const html = composeOfferHtml({
        slug:`${o.slug}-vis`, customerName:name, title:o.title, eventLabel,
        modules:selModules, sectionMap:toSectionMap(fresh),
        quote: p.pris ? o.quote : { lines:[], notes:"" },
        contractHtml: p.kontrakt ? o.contract_html : "",
        riderHtml: p.rider ? o.rider_html : "",
        language:o.language||"no", validUntil:null, presentation:true,
      });
      const blob = new Blob([html], { type:"text/html" });
      const { error } = await supabase.storage.from(BUCKET).upload(`offers/${o.slug}-vis/index.html`, blob, { contentType:"text/html", cacheControl:"30", upsert:true });
      if(error) throw new Error(error.message);
      logActivity(user, 'offer_present_link', { customer:name, slug:`${o.slug}-vis` }, 'offers');
      setPresent(cur=> cur && cur.offer.id===o.id ? { ...cur, busy:false, exists:true, link:`${o.slug}-vis` } : cur);
      showToast("Presentasjonslenke klar! 🎉");
    } catch(e){ showToast("Kunne ikke lage lenke: "+(e.message||e), false); setPresent(cur=> cur?{...cur,busy:false}:cur); }
  };
  const removePresent = async () => {
    const p = present; if(!p) return;
    if(!window.confirm("Fjerne presentasjonslenken? Den slutter å virke umiddelbart.")) return;
    setPresent({ ...p, busy:true });
    const { error } = await supabase.storage.from(BUCKET).remove([`offers/${p.offer.slug}-vis/index.html`]);
    if(error){ showToast("Kunne ikke fjerne.", false); setPresent(cur=>cur?{...cur,busy:false}:cur); return; }
    setPresent(cur=> cur && cur.offer.id===p.offer.id ? { ...cur, busy:false, exists:false, link:null } : cur);
    showToast("Presentasjonslenke fjernet.");
  };

  // Aksept-detaljer (signatur + audit) for et akseptert tilbud
  const viewAcceptance = async (o) => {
    setAcceptance({ offer:o, loading:true });
    const { data } = await supabase.from('offer_acceptances').select('*').eq('offer_id', o.id).order('accepted_at',{ascending:false}).limit(1).maybeSingle();
    setAcceptance({ offer:o, rec:data||null });
  };
  const fmtDateTime = d => { try { return new Date(d).toLocaleString("nb-NO",{day:"numeric",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit"}); } catch { return d||""; } };

  // Maler
  const cleanModulesForTemplate = () => concept.map(b=> b.type==="section"
    ? { id:b.id, type:"section", key:b.key, label:b.label, ...(b.key==="artister"&&Array.isArray(b.artists)&&b.artists.length?{artists:b.artists}:{}) }
    : { id:b.id, type:b.type||"image", eyebrow:b.eyebrow||"", title:b.title||"", text:b.text||"", image_url:b.image_url||"", video_url:b.video_url||"", audio_url:b.audio_url||"", images:(b.images||[]).map(im=>im.image_url).filter(Boolean) });
  const saveAsTemplate = async () => {
    const name = window.prompt("Gi malen et navn:");
    if(!name || !name.trim()) return;
    const cleanLines = lines.filter(l=>(l.desc||"").trim()).map(l=>({ desc:l.desc, qty:Number(l.qty)||0, unit_price:Number(l.unit_price)||0 }));
    const { error } = await supabase.from('offer_templates').insert({ name:name.trim(), language, concept:cleanModulesForTemplate(), quote:{lines:cleanLines,notes}, contract_html:contractHtml, rider_html:riderHtml, created_by:user?.id||null });
    if(error){ showToast("Kunne ikke lagre mal: "+error.message, false); return; }
    showToast("Mal lagret."); loadTemplates();
  };
  const deleteTemplate = async (t) => {
    if(!window.confirm(`Slette malen «${t.name}»?`)) return;
    const { error } = await supabase.from('offer_templates').delete().eq('id', t.id);
    if(error){ showToast("Kunne ikke slette.", false); return; }
    showToast("Mal slettet."); setTemplates(p=>p.filter(x=>x.id!==t.id));
  };

  // Forhåndsvisning (bruker object-URL for ventende bilder)
  const openPreview = () => {
    const name = custName(customerId) || (language==="en"?"Client":"Kunde");
    const previewModules = concept.map(b=>{
      if(b.type==="section") return { type:"section", key:b.key, ...(b.key==="artister"&&Array.isArray(b.artists)&&b.artists.length?{artists:b.artists}:{}) };
      const m = { type:b.type||"image", eyebrow:b.eyebrow, title:b.title, text:b.text, video_url:b.video_url, image_url: b._file&&b.type!=="audio"?URL.createObjectURL(b._file):b.image_url };
      if(b.type==="audio") m.audio_url = b._file?URL.createObjectURL(b._file):b.audio_url;
      if(b.images) m.images = b.images.map(im=>im._file?URL.createObjectURL(im._file):im.image_url).filter(Boolean);
      return m;
    });
    const cleanLines = lines.filter(l=>(l.desc||"").trim()).map(l=>({ desc:l.desc, qty:Number(l.qty)||0, unit_price:Number(l.unit_price)||0 }));
    const proj = projById(projectId);
    const eventLabel = proj ? `${proj.name}${proj.date?` · ${fmtDate(proj.date)}`:""}${proj.location?` · ${proj.location}`:""}` : "";
    const html = composeOfferHtml({ slug:"preview", customerName:name, title, eventLabel, modules:previewModules, sectionMap:toSectionMap(sectionsLib), quote:{lines:cleanLines,notes}, contractHtml, riderHtml, language, validUntil:validUntil||null, preview:true });
    setPreview(html);
  };

  // ===================== BUILDER =====================
  if(view==="builder"){
    const projOptions = custProjects(customerId);
    return (
      <div style={{paddingBottom:isMobile?80:0,maxWidth:720}}>
        <Toast msg={toast.msg} ok={toast.ok}/>
        {gradBar}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:20,fontWeight:500}}>{editing?"Rediger tilbud":"Nytt tilbud"}</div>
          <button style={{...btn("ghost"),padding:"6px 14px",fontSize:12}} onClick={()=>setView("list")}>← Tilbake</button>
        </div>

        {/* Kunde + prosjekt + tittel */}
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Kunde & prosjekt")}
          {lbl("Kunde *")}
          <div style={{marginBottom:14}}><CustomerSelect customers={customers} value={customerId} onCreate={onCreateCustomer} required onChange={onCustomer}/></div>
          {lbl("Prosjekt * (kreves for aksept)")}
          <select style={{...inp(),marginBottom:6,appearance:"none"}} value={projectId} onChange={e=>setProjectId(e.target.value)} disabled={!customerId}>
            <option value="">{customerId?"— Velg prosjekt —":"velg kunde først"}</option>
            {projOptions.map(p=><option key={p.id} value={p.id}>{p.name} · {fmtDate(p.date)}</option>)}
          </select>
          {customerId && projOptions.length===0 && <div style={{fontSize:12,color:C.gold,marginBottom:8}}>Kunden har ingen prosjekter ennå – opprett et event og koble det til kunden først.</div>}
          <div style={{marginTop:8}}>{lbl("Tittel på tilbudet")}
            <input style={inp()} placeholder="f.eks. Underholdning til sommerfest 2026" value={title} onChange={e=>setTitle(e.target.value)}/></div>
          <div style={{marginTop:14}}>{lbl("Språk på tilbudssiden")}
            <div style={{display:"flex",gap:8}}>
              {LANGS.map(l=>(
                <button key={l.code} type="button" onClick={()=>switchLanguage(l.code)}
                  style={{...btn(language===l.code?"primary":"ghost"),padding:"6px 16px",fontSize:12}}>{l.label}</button>
              ))}
            </div>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>Faste etiketter (Pris, Kontrakt, Aksepter …) settes på valgt språk. Konsept/notater skriver du selv.</div>
          </div>
          <div style={{marginTop:14}}>{lbl("Gyldig til (valgfritt)")}
            <input style={{...inp(),maxWidth:200}} type="date" value={validUntil||""} onChange={e=>setValidUntil(e.target.value)}/>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>Vises på tilbudet, og aksept blokkeres etter denne datoen.</div>
          </div>
        </div>

        {/* Moduler */}
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Innhold (moduler)")}
          {concept.length===0 && <div style={{fontSize:12,color:C.muted,marginBottom:10}}>Bygg siden av moduler i valgfri rekkefølge. Pris, kontrakt, rider og aksept legges automatisk nederst.</div>}
          {concept.map((b,i)=>(
            <div key={b.id} style={{borderTop:i>0?`0.5px solid ${C.border}`:"none",paddingTop:i>0?14:0,marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{fontSize:11,color:b.type==="section"?C.gold:C.magenta,textTransform:"uppercase",letterSpacing:0.5,fontWeight:600}}>{i+1} · {b.type==="section"?(b.label||"Seksjon"):TYPE_LABEL[b.type||"image"]}</span>
                <button onClick={()=>moveModule(i,-1)} disabled={i===0} style={{...btn("ghost"),padding:"1px 8px",fontSize:11,opacity:i===0?0.3:1}}>↑</button>
                <button onClick={()=>moveModule(i,1)} disabled={i===concept.length-1} style={{...btn("ghost"),padding:"1px 8px",fontSize:11,opacity:i===concept.length-1?0.3:1}}>↓</button>
                <button onClick={()=>rmModule(i)} style={{...btn("danger"),padding:"3px 9px",fontSize:11,marginLeft:"auto"}}>Fjern</button>
              </div>
              {b.type==="section" ? (
                <div style={{fontSize:12,color:C.muted}}>Standardseksjon «{b.label}» fra kundeportalen.
                  {language==="en" && !((sectionsLib.find(s=>s.key===b.key)||{}).html_en||"").trim() && <span style={{color:C.gold}}> ⚠ Mangler engelsk versjon – vises på norsk. Oversett i «Rediger seksjoner».</span>}
                  {b.key==="artister" && (()=>{
                    const all = extractArtists((sectionsLib.find(s=>s.key==="artister")||{}).html||"");
                    if(!all.length) return null;
                    const subset = Array.isArray(b.artists) && b.artists.length>0;
                    const isOn = name => !subset || b.artists.includes(name);
                    const toggle = name => {
                      const cur = new Set(subset ? b.artists : all.map(a=>a.name));
                      cur.has(name) ? cur.delete(name) : cur.add(name);
                      const next = all.map(a=>a.name).filter(n=>cur.has(n));  // behold rekkefølgen
                      updModule(i,{ artists: next.length===all.length ? null : next });
                    };
                    return (
                      <div style={{marginTop:10}}>
                        <div style={{fontSize:11,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Hvilke artister skal være med?</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                          {all.map(a=>{ const on=isOn(a.name); return (
                            <label key={a.name} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:on?C.white:C.muted,background:C.surface2,border:`0.5px solid ${on?C.magenta:C.border}`,borderRadius:8,padding:"5px 10px",cursor:"pointer"}}>
                              <input type="checkbox" checked={on} onChange={()=>toggle(a.name)} style={{accentColor:C.magenta,cursor:"pointer"}}/>
                              {a.name}{a.role?<span style={{color:C.muted,fontWeight:400}}> · {a.role}</span>:null}
                            </label>
                          );})}
                        </div>
                        <div style={{fontSize:11,color:subset?C.gold:C.muted,marginTop:6}}>{subset?`${b.artists.length} av ${all.length} artister valgt.`:"Alle artister vises (huk av for å begrense)."}</div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <input style={{...inp(),marginBottom:8}} placeholder="Liten overskrift (valgfri)" value={b.eyebrow} onChange={e=>updModule(i,{eyebrow:e.target.value})}/>
                  <input style={{...inp(),marginBottom:8}} placeholder="Tittel" value={b.title} onChange={e=>updModule(i,{title:e.target.value})}/>
                  <textarea style={{...inp(),marginBottom:8,minHeight:70,resize:"vertical",fontFamily:"inherit"}} placeholder="Tekst… (tom linje = nytt avsnitt)" value={b.text} onChange={e=>updModule(i,{text:e.target.value})}/>
                  {(b.type==="image"||b.type==="split") && (
                    <>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                      <label style={{...btn("ghost"),padding:"6px 12px",fontSize:12,cursor:"pointer"}}>{b._file?`📎 ${b._file.name.slice(0,18)}`:(b.image_url?"Bytt bilde":"Last opp bilde")}<input type="file" accept="image/*" style={{display:"none"}} onChange={e=>updModule(i,{_file:e.target.files?.[0]||null})}/></label>
                      {b.type==="image" && <input style={{...inp(),flex:"1 1 160px",width:"auto"}} placeholder="Video-URL (valgfritt)" value={b.video_url} onChange={e=>updModule(i,{video_url:e.target.value})}/>}
                      {b.type==="split" && <span style={{fontSize:11,color:C.muted}}>Bilde + tekst side ved side (veksler side automatisk).</span>}
                    </div>
                    {b.type==="image" && b.video_url && <div style={{fontSize:11,color:C.muted,marginTop:6}}>Med video-URL vises et video-kort. Last opp et bilde for å bruke det som thumbnail – ellers hentes thumbnail automatisk fra Vimeo/YouTube.</div>}
                    </>
                  )}
                  {b.type==="audio" && (
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                      <label style={{...btn("ghost"),padding:"6px 12px",fontSize:12,cursor:"pointer"}}>{b._file?`🎵 ${b._file.name.slice(0,22)}`:(b.audio_url?"Bytt lydfil":"Last opp lydfil")}<input type="file" accept="audio/*" style={{display:"none"}} onChange={e=>updModule(i,{_file:e.target.files?.[0]||null})}/></label>
                      {(b._file||b.audio_url) && <audio controls src={b._file?URL.createObjectURL(b._file):b.audio_url} style={{height:34,maxWidth:240}}/>}
                      {!b._file && !b.audio_url && <span style={{fontSize:11,color:C.muted}}>MP3, WAV, M4A … spilles av med en spiller på tilbudssiden.</span>}
                    </div>
                  )}
                  {b.type==="gallery" && (
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                      {(b.images||[]).map((im,k)=>(
                        <div key={k} style={{position:"relative"}}>
                          <img src={im._file?URL.createObjectURL(im._file):im.image_url} alt="" style={{width:54,height:54,objectFit:"cover",borderRadius:6,display:"block"}}/>
                          <button onClick={()=>rmGalleryImg(i,k)} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:9,background:C.red,color:"#fff",border:"none",fontSize:11,cursor:"pointer",lineHeight:1}}>×</button>
                        </div>
                      ))}
                      <label style={{...btn("ghost"),padding:"6px 12px",fontSize:12,cursor:"pointer"}}>+ Bilde<input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{ if(e.target.files?.[0]) addGalleryImg(i,e.target.files[0]); e.target.value=""; }}/></label>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:concept.length?6:0}}>
            {MODULE_TYPES.map(t=>(
              <button key={t.type} style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>addModule(t.type)}>+ {t.label}</button>
            ))}
            <select style={{...inp(),width:"auto",appearance:"none",padding:"6px 12px",fontSize:12,cursor:"pointer"}} value="" onChange={e=>{ if(e.target.value){ addSection(e.target.value); } }}>
              <option value="">+ Standardseksjon…</option>
              {sectionsLib.filter(s=>OFFER_SECTION_KEYS.includes(s.key)).map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Pris */}
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Pris / tilbud")}
          <div style={{display:"flex",gap:8,fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>
            <span style={{flex:1}}>Beskrivelse</span><span style={{width:60}}>Antall</span><span style={{width:110}}>Enhetspris</span><span style={{width:30}}></span>
          </div>
          {lines.map((l,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
              <input style={{...inp(),flex:1}} placeholder="f.eks. Artist (5 dansere + vokalist)" value={l.desc} onChange={e=>updLine(i,"desc",e.target.value)}/>
              <input style={{...inp(),width:60}} type="number" value={l.qty} onChange={e=>updLine(i,"qty",e.target.value)}/>
              <input style={{...inp(),width:110}} type="number" value={l.unit_price} onChange={e=>updLine(i,"unit_price",e.target.value)}/>
              <button style={{...btn("danger"),padding:"6px 10px",fontSize:12}} onClick={()=>rmLine(i)}>×</button>
            </div>
          ))}
          <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12,marginTop:2}} onClick={addLine}>+ Legg til linje</button>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:14,paddingTop:14,borderTop:`0.5px solid ${C.border}`}}>
            <span style={{color:C.muted,fontSize:13}}>Totalt (eks. mva)</span>
            <span style={{color:C.green,fontSize:20,fontWeight:600}}>{fmtNok(total)} NOK</span>
          </div>
          <div style={{marginTop:14}}>{lbl("Notat under prisen (valgfritt)")}
            <textarea style={{...inp(),minHeight:50,resize:"vertical"}} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="f.eks. Reise og opphold kommer i tillegg etter avtale."/></div>
        </div>

        {/* Kontrakt + rider */}
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Kontrakt")}
          <textarea style={{...inp(),minHeight:160,resize:"vertical",fontFamily:"inherit",lineHeight:1.5}} value={contractHtml} onChange={e=>setContractHtml(e.target.value)}/>
        </div>
        <div style={{...card(),marginBottom:18}}>
          {secTitle("Rider")}
          <textarea style={{...inp(),minHeight:140,resize:"vertical",fontFamily:"inherit",lineHeight:1.5}} value={riderHtml} onChange={e=>setRiderHtml(e.target.value)}/>
        </div>

        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button style={{...btn("primary"),opacity:saving?0.6:1,pointerEvents:saving?"none":"auto"}} onClick={saveBuilder}>{saving?"Publiserer…":(editing?"Lagre & publiser":"Generer & publiser")}</button>
          <button style={btn("ghost")} onClick={openPreview}>Forhåndsvis</button>
          <button style={btn("ghost")} onClick={saveAsTemplate}>Lagre som mal</button>
          <button style={btn("ghost")} onClick={()=>setView("list")}>Avbryt</button>
        </div>

        {preview && (
          <div onClick={()=>setPreview(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:300,display:"flex",flexDirection:"column",padding:isMobile?0:24}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"#0a0a0c",borderRadius:isMobile?0:12,flex:1,display:"flex",flexDirection:"column",overflow:"hidden",maxWidth:1100,width:"100%",margin:"0 auto"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 16px",background:C.surface,borderBottom:`0.5px solid ${C.border}`,flexShrink:0}}>
                <span style={{fontSize:13,color:C.muted}}>Forhåndsvisning</span>
                <button style={{...btn("ghost"),padding:"5px 12px",fontSize:12}} onClick={()=>setPreview(null)}>Lukk ×</button>
              </div>
              <iframe title="forhåndsvisning" srcDoc={preview} style={{flex:1,border:"none",width:"100%",background:"#0a0a0c"}}/>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===================== MALER =====================
  if(view==="templates"){
    return (
      <div style={{paddingBottom:isMobile?80:0}}>
        <Toast msg={toast.msg} ok={toast.ok}/>
        {gradBar}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{fontSize:20,fontWeight:500}}>Tilbuds-maler</div>
          <button style={{...btn("ghost"),padding:"6px 14px",fontSize:12}} onClick={()=>setView("list")}>← Tilbake</button>
        </div>
        <div style={{fontSize:13,color:C.muted,marginBottom:20,maxWidth:620}}>Gjenbrukbare oppsett. Lag en mal med «Lagre som mal» i et tilbud, og start nye tilbud fra den (sparer mest tid).</div>
        <div style={{maxWidth:640}}>
          {templates.length===0 && <div style={{color:C.muted,fontSize:13}}>Ingen maler ennå. Åpne et tilbud i builderen og trykk «Lagre som mal».</div>}
          {templates.map(t=>(
            <div key={t.id} style={{...card(),marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:15,fontWeight:500}}>{t.name} <span style={{fontSize:12,color:C.muted}}>· {t.language==="en"?"English":"Norsk"}</span></div>
                <div style={{fontSize:11,color:C.muted}}>{(t.concept||[]).length} moduler · {((t.quote&&t.quote.lines)||[]).length} prislinjer</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button style={{...btn("primary"),padding:"6px 14px",fontSize:12}} onClick={()=>openBuilder(t, null)}>Bruk</button>
                <button style={{...btn("danger"),padding:"6px 12px",fontSize:12}} onClick={()=>deleteTemplate(t)}>Slett</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ===================== LISTE =====================
  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {gradBar}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:20,fontWeight:500}}>Tilbud</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {templates.length>0 && (
            <select style={{...inp(),width:"auto",appearance:"none",padding:"7px 12px",fontSize:13,cursor:"pointer"}} value="" onChange={e=>{ const t=templates.find(x=>x.id===e.target.value); if(t) openBuilder(t, null); }}>
              <option value="">Start fra mal…</option>
              {templates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <button style={{...btn("ghost"),padding:"7px 14px",fontSize:13}} onClick={()=>setView("templates")}>Maler</button>
          {(trash.length>0||showTrash) && <button style={{...btn("ghost"),padding:"7px 14px",fontSize:13,...(showTrash?{color:C.orange,borderColor:`${C.orange}66`}:{})}} onClick={()=>setShowTrash(v=>!v)}>
            {showTrash?"← Tilbake":`Papirkurv (${trash.length})`}
          </button>}
          <button style={{...btn("primary"),padding:"7px 16px",fontSize:13}} onClick={()=>openBuilder(null)}>+ Nytt tilbud</button>
        </div>
      </div>
      <div style={{fontSize:13,color:C.muted,marginBottom:24,maxWidth:620}}>
        Skreddersydde tilbud per kunde og event: konsept, pris, kontrakt og rider på én delbar side.
      </div>
      {showTrash ? (
        <div style={{maxWidth:640}}>
          <div style={{fontSize:13,color:C.muted,marginBottom:16}}>
            Slettede tilbud. Gjenoppretting bygger lenken opp igjen med bildene i behold.
          </div>
          {trash.length===0 && <div style={{color:C.muted,fontSize:13,padding:"8px 0"}}>Papirkurven er tom.</div>}
          {trash.map(o=>(
            <div key={o.id} style={{...card({border:`0.5px solid ${C.border}`}),marginBottom:10,opacity:0.85}}>
              <div style={{fontSize:15,fontWeight:500,marginBottom:2}}>{custName(o.customer_id)||"—"}</div>
              {o.title && <div style={{fontSize:13,color:C.muted,marginBottom:2}}>{o.title}</div>}
              <div style={{fontSize:11,color:C.muted}}>Slettet {fmtDateTime(o.deleted_at)} · {fmtNok(quoteTotal(o.quote))} NOK</div>
              <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
                <button style={{...btn("primary"),padding:"6px 12px",fontSize:12}} disabled={restoring===o.id} onClick={()=>restoreOffer(o)}>
                  {restoring===o.id?"Gjenoppretter…":"Gjenopprett"}
                </button>
                <button style={{...btn("danger"),padding:"6px 12px",fontSize:12}} onClick={()=>purgeOffer(o)}>Slett permanent</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div style={{maxWidth:640}}>
        {loading && <div style={{color:C.muted,fontSize:13,padding:"8px 0"}}>Laster…</div>}
        {!loading && offers.length===0 && <div style={{color:C.muted,fontSize:13,padding:"8px 0"}}>Ingen tilbud ennå.</div>}
        {offers.map(o=>{
          const st = STATUS[o.status] || STATUS.draft;
          return (
            <div key={o.id} style={{...card(),marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{fontSize:15,fontWeight:500,marginBottom:2}}>{custName(o.customer_id)||"—"} <span style={{fontSize:12,color:st.color}}>· {st.label}</span></div>
                  {o.title && <div style={{fontSize:13,color:C.muted,marginBottom:2}}>{o.title}</div>}
                  <div style={{fontSize:12,color:C.blue,wordBreak:"break-all",marginBottom:4}}>{OFFER_BASE}{o.slug}</div>
                  <div style={{fontSize:11,color:C.muted}}>{fmtNok(quoteTotal(o.quote))} NOK{o.project_id&&projById(o.project_id)?` · ${projById(o.project_id).name}`:""}</div>
                  {(()=>{ const s=viewStats(o.slug); return (
                    <div style={{fontSize:11,marginTop:4,color:s.opens?C.green:C.gold}}>
                      {s.opens
                        ? `👁 Åpnet ${s.opens} ${s.opens===1?"gang":"ganger"} · sist ${fmtDateTime(s.last)}`
                        : "Ikke åpnet ennå"}
                    </div>
                  ); })()}
                  {o.status==="accepted" && <div style={{fontSize:11,color:C.green,marginTop:4}}>✓ Akseptert{o.accepted_by?` av ${o.accepted_by}`:""}{o.accepted_at?` · ${fmtDateTime(o.accepted_at)}`:""}</div>}
                  {o.status==="declined" && <div style={{fontSize:11,color:C.red,marginTop:4}}>✕ Avslått{o.declined_by?` av ${o.declined_by}`:""}{o.decline_reason?` · «${o.decline_reason}»`:""}</div>}
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
                {o.status==="accepted" && <button style={{...btn("primary"),padding:"6px 12px",fontSize:12}} onClick={()=>viewAcceptance(o)}>Se signatur</button>}
                <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>openBuilder(o, o)}>Rediger</button>
                <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>openBuilder(o, null)}>Dupliser</button>
                {(views[o.slug]||[]).length>0 && <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>setViewsFor(o)}>Visninger</button>}
                <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>copyLink(o.slug)}>Kopier lenke</button>
                <button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>openPresent(o)}>Presentasjonslenke</button>
                <a href={OFFER_BASE+o.slug} target="_blank" rel="noreferrer" style={{...btn("ghost"),padding:"6px 12px",fontSize:12,textDecoration:"none",color:C.muted}}>Åpne</a>
                {o.project_id&&projById(o.project_id)&&onOpenProject&&<button style={{...btn("ghost"),padding:"6px 12px",fontSize:12}} onClick={()=>onOpenProject(projById(o.project_id))}>Prosjekt</button>}
                <button style={{...btn("danger"),padding:"6px 12px",fontSize:12}} onClick={()=>deleteOffer(o)}>Slett</button>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {viewsFor && (
        <div onClick={()=>setViewsFor(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?12:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:12,border:`0.5px solid ${C.border}`,maxWidth:560,width:"100%",maxHeight:"90vh",overflow:"auto",padding:22}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:16,fontWeight:600}}>Visninger</div>
              <button style={{...btn("ghost"),padding:"5px 12px",fontSize:12}} onClick={()=>setViewsFor(null)}>Lukk ×</button>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{custName(viewsFor.customer_id)}{viewsFor.title?` · ${viewsFor.title}`:""}</div>
            {(views[viewsFor.slug]||[]).map(v=>(
              <div key={v.id} style={{padding:"10px 12px",background:C.surface2,borderRadius:8,marginBottom:8,borderLeft:`3px solid ${v.js_confirmed?C.green:C.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:4}}>
                  <div style={{fontSize:13,fontWeight:500}}>{fmtDateTime(v.viewed_at)}</div>
                  <div style={{fontSize:11,fontFamily:"monospace",color:C.blue}}>{v.ip||"–"}</div>
                </div>
                <div style={{fontSize:11,color:C.muted}}>
                  {[v.city,v.country].filter(Boolean).join(", ")||"Ukjent sted"}
                  {v.slug.endsWith("-vis")?" · presentasjonslenke":""}
                </div>
                <div style={{fontSize:10,color:"#555",marginTop:3,wordBreak:"break-all"}}>{v.user_agent||""}</div>
                {!v.js_confirmed && <div style={{fontSize:10,color:C.gold,marginTop:4}}>Kun hentet – trolig lenkeforhåndsvisning, ikke en person</div>}
              </div>
            ))}
            <div style={{fontSize:11,color:C.muted,marginTop:12,lineHeight:1.6}}>
              Grønn strek = bekreftet åpnet i en nettleser. Rader uten er som regel
              e-post- eller chat-tjenester som henter lenken automatisk.
            </div>
          </div>
        </div>
      )}

      {acceptance && (
        <div onClick={()=>setAcceptance(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?12:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:12,border:`0.5px solid ${C.border}`,maxWidth:460,width:"100%",maxHeight:"90vh",overflow:"auto",padding:22}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:600}}>Aksept & signatur</div>
              <button style={{...btn("ghost"),padding:"5px 12px",fontSize:12}} onClick={()=>setAcceptance(null)}>Lukk ×</button>
            </div>
            {acceptance.loading ? (
              <div style={{color:C.muted,fontSize:13,padding:"20px 0",textAlign:"center"}}>Laster…</div>
            ) : !acceptance.rec ? (
              <div style={{color:C.muted,fontSize:13}}>Fant ingen lagret aksept for dette tilbudet.</div>
            ) : (() => {
              const r = acceptance.rec;
              const Row = ({k,v}) => v ? <div style={{display:"flex",gap:10,marginBottom:8,fontSize:13}}><span style={{color:C.muted,minWidth:96,flexShrink:0}}>{k}</span><span style={{wordBreak:"break-word"}}>{v}</span></div> : null;
              return (
                <>
                  <Row k="Navn" v={r.acceptor_name}/>
                  <Row k="Rolle" v={r.acceptor_role}/>
                  <Row k="Firma" v={r.acceptor_company}/>
                  <Row k="E-post" v={r.acceptor_email}/>
                  <Row k="Tidspunkt" v={fmtDateTime(r.accepted_at)}/>
                  {r.affirmation && <div style={{margin:"12px 0",padding:"10px 12px",background:C.surface2,borderRadius:8,fontSize:12,color:C.muted,fontStyle:"italic"}}>«{r.affirmation}»</div>}
                  <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,margin:"14px 0 6px"}}>Signatur</div>
                  {r.signature
                    ? <img src={r.signature} alt="Signatur" style={{width:"100%",maxWidth:360,background:"#fff",borderRadius:8,border:`0.5px solid ${C.border}`,display:"block"}}/>
                    : <div style={{fontSize:12,color:C.muted}}>Ingen tegnet signatur (eldre aksept).</div>}
                  <div style={{marginTop:16,paddingTop:12,borderTop:`0.5px solid ${C.border}`,fontSize:11,color:C.muted,lineHeight:1.6}}>
                    <div>Revisjonsspor:</div>
                    {r.ip && <div>IP: {r.ip}</div>}
                    {r.user_agent && <div style={{wordBreak:"break-word"}}>Enhet: {r.user_agent}</div>}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {present && (
        <div onClick={()=>!present.busy&&setPresent(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:isMobile?12:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:12,border:`0.5px solid ${C.border}`,maxWidth:480,width:"100%",maxHeight:"90vh",overflow:"auto",padding:22}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:16,fontWeight:600}}>Presentasjonslenke</div>
              <button style={{...btn("ghost"),padding:"5px 12px",fontSize:12}} onClick={()=>!present.busy&&setPresent(null)}>Lukk ×</button>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:16,lineHeight:1.5}}>
              En egen, delbar side <strong>uten aksept/signering</strong> – huk av hva som skal være med. Det opprinnelige tilbudet røres ikke.
            </div>

            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Innhold</div>
            {present.mods.length===0 && <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Tilbudet har ingen innholdsmoduler.</div>}
            {present.mods.map(m=>(
              <label key={m.key} style={{display:"flex",alignItems:"center",gap:9,fontSize:13,color:m.on?C.white:C.muted,padding:"6px 0",cursor:"pointer"}}>
                <input type="checkbox" checked={m.on} onChange={()=>toggleMod(m.key)} style={{accentColor:C.magenta,cursor:"pointer"}}/>
                {m.label}
              </label>
            ))}

            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5,margin:"16px 0 8px"}}>Seksjoner</div>
            {[["pris","Pris"],["kontrakt","Kontrakt"],["rider","Rider (teknisk)"]].map(([k,label])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:9,fontSize:13,color:present[k]?C.white:C.muted,padding:"6px 0",cursor:"pointer"}}>
                <input type="checkbox" checked={present[k]} onChange={()=>setPresent(p=>({...p,[k]:!p[k]}))} style={{accentColor:C.magenta,cursor:"pointer"}}/>
                {label}
              </label>
            ))}
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>Aksept og signering er aldri med på en presentasjonslenke.</div>

            {present.link && (
              <div style={{marginTop:16,padding:"10px 12px",background:C.surface2,borderRadius:8,fontSize:12,color:C.blue,wordBreak:"break-all"}}>
                {OFFER_BASE}{present.link}
              </div>
            )}

            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:16}}>
              <button style={{...btn("primary"),padding:"8px 14px",fontSize:13,opacity:present.busy?0.6:1,pointerEvents:present.busy?"none":"auto"}} onClick={genPresent}>
                {present.busy?"Genererer…":(present.exists?"Oppdater lenke":"Generer lenke")}
              </button>
              {present.link && <>
                <button style={{...btn("ghost"),padding:"8px 14px",fontSize:13}} onClick={()=>copyLink(present.link)}>Kopier</button>
                <a href={OFFER_BASE+present.link} target="_blank" rel="noreferrer" style={{...btn("ghost"),padding:"8px 14px",fontSize:13,textDecoration:"none",color:C.muted}}>Åpne</a>
                <button style={{...btn("danger"),padding:"8px 14px",fontSize:13}} onClick={removePresent}>Fjern</button>
              </>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Toast({ msg, ok=true }) {
  if(!msg) return null;
  return <div style={{position:"fixed",bottom:72,left:"50%",transform:"translateX(-50%)",background:C.surface,border:`0.5px solid ${ok?C.green:C.red}44`,color:ok?C.green:C.red,padding:"10px 20px",borderRadius:20,fontSize:13,zIndex:400,whiteSpace:"nowrap"}}>{msg}</div>;
}
